import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { multiStep } from '../../MultiStepWizard/multiStep'
import { SelectedImage } from 'types'
import { Provider } from 'components/ImagePickerModal/Types'
import { ImagePickerProvider, useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Uploads } from 'components/ImagePickerModal/Uploads/Uploads'
import { Form } from 'components/Wizards/Form/Form'
import DesignMarketplaceWizardStyles from '../DesignMarketplaceWizard.module.scss'
import Styles from './SelectImages.module.scss'
import { Formik, useFormikContext } from 'formik'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { Button } from 'blaze-ui/Button'
import cx from 'classnames'
import { LinkAngled } from 'blaze-ui/icons'
import { Link } from 'components/ImagePickerModal/Link/Link'
import { useEffect, useState } from 'react'
import { useConfirm } from '../../DesignWizard/AddPhoto/useConfirm'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Upload5 } from 'blaze-ui/icons/30'
import { getTemplatePreviewCloudinaryUrl } from 'components/Design/utils/getTemplatePreviewCloudinaryUrl'
import { DismissToast } from 'common/ToastMessage'
import { FilePathFormikField } from '../../FilePathFormikField/FilePathFormikField'
import { Values } from '../../DesignWizard/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { SelectImageMobile } from './SelectImageMobile'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'

interface Props extends StepProps {
  isLoading?: boolean
  onImageChange: (image: SelectedImage) => void
  setCanRemoveBackground: (value: boolean) => void
  createDesign: (values: Values) => Promise<void>
}

interface ContentProps extends Props {
  isUploading: boolean
  setIsUploading: (value: boolean) => void
  uploadedImageBase64: string
}

export const SelectImage = multiStep<Props>((props) => {
  const { initialValues, onImageChange, schema, onSubmit } = props
  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedImageBase64, setUploadedImageBase64] = useState('')
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { isMobile } = useResponsiveDesign()

  let uploadingToast: DismissToast

  const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
    })

  if (isMobile) return <SelectImageMobile {...props} />

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault() }, { assert: false })}
    >
      {({ setFieldValue }) => (
        <ImagePickerProvider
          defaultProvider="uploads"
          maxCount={1}
          accept={ACCEPTED_IMAGE_MIME_TYPES_NO_SVG}
          uploadImages={async (files) => {
            if (!uploadingToast) uploadingToast = showToastMessage('Uploading image...', 'generating', undefined, 0)
            const uploadedImages = await uploadImageFilesWithoutLoadingToast(files, uploadsProjectId)
            uploadingToast?.()

            if (!uploadedImages) return []
            return uploadedImages.map((file) => ({ id: file.id, url: file.source_url } as SelectedImage))
          }}
          initialQuery={initialValues.topic || ''}
          onUploadStart={async (files) => {
            const base64Url = (await toBase64(files[0])) as string
            setUploadedImageBase64(base64Url)
            setIsUploading(true)
          }}
          onConfirm={async (images) => {
            if (images[0]?.id) setFieldValue('initial_media_file_id', images[0].id)
            setIsUploading(false)
            onImageChange(images[0])
          }}
          hiddenProviders={new Set<Provider>(['gifs', 'ai'])}
        >
          <Content
            {...props}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadedImageBase64={uploadedImageBase64}
          />
        </ImagePickerProvider>
      )}
    </Formik>
  )
})

const Content = multiStep<ContentProps>(
  ({
    schema,
    onImageChange,
    isUploading,
    setIsUploading,
    setCanRemoveBackground,
    uploadedImageBase64,
    createDesign,
    initialValues,
  }) => {
    const { provider, setProvider, selectedImages } = useImagePickerContext()
    const { isSubmitting, submitForm, setFieldValue, isValid } = useFormikContext()
    const [isGenerating, setIsGenerating] = useState(false)
    const [imageFromAllUploads, setImageFromAllUploads] = useState<SelectedImage>()
    const { confirm } = useConfirm()
    const startWithTemplate = initialValues.startWithTemplate

    const handleSubmit = () => {
      if (selectedImages[0]?.id) setFieldValue('initial_media_file_id', selectedImages[0].id)

      // Need to add a small timeout before submitting right after the required field is set in the line above,
      // otherwise the submit validation doesn't pass and the step is not submitted
      setTimeout(() => {
        submitForm()
        setCanRemoveBackground(true)
      }, 100)
    }

    useEffect(() => {
      if (selectedImages[0]) onImageChange(selectedImages[0])
    }, [selectedImages]) // eslint-disable-line react-hooks/exhaustive-deps

    const imageUrl = selectedImages[0]?.source_url || selectedImages[0]?.url
    const url = imageUrl ? getTemplatePreviewCloudinaryUrl(imageUrl, { height: 350 }) : uploadedImageBase64
    const isDisabled = isSubmitting || isUploading || isGenerating

    const handleStartWithBlankCanvas = async () => {
      setIsGenerating(true)
      await createDesign({
        ...initialValues,
        initial_media_file_id: undefined,
        filename: 'Blank Canvas',
      }).finally(() => {
        setIsGenerating(false)
      })
    }

    return (
      <Form.Root>
        <Form.Section>
          <div className={cx(DesignMarketplaceWizardStyles.container, { [Styles.isUploading]: isUploading })}>
            {isUploading ? (
              <LoadingShimmerer className={Styles.selectedImage}>
                <img alt="selected" src={url} />
              </LoadingShimmerer>
            ) : (
              <BaseField
                label="Upload your image"
                isRequired
                rightContent={
                  <>
                    {provider === 'link' ? (
                      <Button variant="tertiary" size="xs" frontIcon={Upload5} onPress={() => setProvider('uploads')}>
                        Upload an image
                      </Button>
                    ) : (
                      <Button variant="tertiary" size="xs" frontIcon={LinkAngled} onPress={() => setProvider('link')}>
                        Link to an image
                      </Button>
                    )}
                  </>
                }
              >
                {provider === 'link' && <Link />}
                {provider === 'uploads' && (
                  <Uploads
                    dropzoneClassName={Styles.dropzone}
                    recentUploadsLabel="Use recently uploaded media"
                    headingLevel={5}
                    wrapperClassName={Styles.uploads}
                    seeAllButtonVariant="secondary"
                    dropzoneButtonVariant="secondary"
                    dropzoneButtonLabel="Choose Files"
                    dropzoneLabelVariant="primary"
                    dropzoneLabel="Drop files here or"
                    newStack
                    showSelected
                    customImages={imageFromAllUploads ? [imageFromAllUploads] : []}
                    onSelectImageFromAll={(images) => setImageFromAllUploads(images[0])}
                  />
                )}
              </BaseField>
            )}
            {startWithTemplate && !isUploading && (
              <BaseField label="Name your post and select a location" isRequired>
                <FilePathFormikField
                  placeholder="Name your design..."
                  locationPickerFullWidth
                  inputClassName={Styles.filenameInput}
                />
              </BaseField>
            )}
          </div>
        </Form.Section>
        <Form.Actions isSticky>
          <Form.PrimaryActions>
            {!startWithTemplate && (
              <Button variant="tertiary" size="lg" isDisabled={isDisabled} onPress={handleStartWithBlankCanvas}>
                Start with a Blank Canvas
              </Button>
            )}
            <Button
              size="lg"
              isDisabled={isDisabled || !selectedImages[0]}
              onPress={async () => {
                if (!isValid) {
                  submitForm()
                  return
                }

                if (provider === 'link') {
                  setIsUploading(true)
                  await confirm() // this will call the link provider's useConfirm hook
                  setIsUploading(false)
                }

                handleSubmit()
              }}
            >
              {schema.describe().meta.submitLabel}
            </Button>
          </Form.PrimaryActions>
        </Form.Actions>
      </Form.Root>
    )
  },
)
